import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { BOARD_CODES_FULL, BOARD_GROUPS, Icons } from "./bookingConstants";

/* ─── Styles (inline — keeps the component self-contained) ──────────── */

const S = {
  wrap: {
    position: "relative",
    width: "100%",
  },
  trigger: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    padding: "9px 12px",
    border: "1.5px solid #e5e7eb",
    borderRadius: 10,
    background: "#fff",
    fontSize: 13,
    fontWeight: 500,
    color: "#111827",
    cursor: "pointer",
    transition: "border-color .15s",
    minHeight: 40,
    boxSizing: "border-box",
  },
  triggerOpen: {
    borderColor: "#6366f1",
    boxShadow: "0 0 0 3px rgba(99,102,241,.12)",
  },
  triggerPlaceholder: {
    color: "#9ca3af",
    fontWeight: 400,
  },
  triggerCode: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 8px",
    borderRadius: 6,
    background: "#eef2ff",
    color: "#4f46e5",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: ".03em",
    flexShrink: 0,
  },
  triggerName: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  triggerIncluded: {
    fontSize: 11,
    color: "#6b7280",
    fontWeight: 400,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 180,
  },
  chevron: {
    flexShrink: 0,
    transition: "transform .2s",
    color: "#9ca3af",
  },
  chevronOpen: {
    transform: "rotate(180deg)",
  },
  dropdown: {
    position: "fixed",
    background: "#fff",
    border: "1.5px solid #e5e7eb",
    borderRadius: 12,
    boxShadow: "0 12px 32px rgba(0,0,0,.12)",
    zIndex: 9999,
    maxHeight: 340,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    animation: "boardDdFadeIn .15s ease",
  },
  searchWrap: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 12px",
    borderBottom: "1px solid #f1f1f1",
    background: "#fafbfc",
  },
  searchIcon: {
    color: "#9ca3af",
    flexShrink: 0,
  },
  searchInput: {
    flex: 1,
    border: "none",
    outline: "none",
    background: "transparent",
    fontSize: 13,
    color: "#111827",
    padding: 0,
    fontFamily: "inherit",
  },
  list: {
    overflowY: "auto",
    flex: 1,
  },
  groupLabel: {
    padding: "8px 14px 4px",
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: ".06em",
    color: "#9ca3af",
    background: "#fafbfc",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  item: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 14px",
    cursor: "pointer",
    transition: "background .1s",
    borderBottom: "1px solid #f7f7f8",
    width: "100%",
    border: "none",
    background: "transparent",
    textAlign: "left",
    fontSize: 13,
    fontFamily: "inherit",
  },
  itemHover: {
    background: "#f0f0ff",
  },
  itemSelected: {
    background: "#eef2ff",
  },
  itemCode: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 7px",
    borderRadius: 5,
    background: "#eef2ff",
    color: "#4f46e5",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: ".03em",
    minWidth: 36,
    justifyContent: "center",
    flexShrink: 0,
  },
  itemInfo: {
    flex: 1,
    minWidth: 0,
  },
  itemName: {
    fontWeight: 600,
    color: "#111827",
    fontSize: 13,
    lineHeight: 1.3,
  },
  itemIncluded: {
    fontSize: 11,
    color: "#6b7280",
    lineHeight: 1.3,
    marginTop: 1,
  },
  empty: {
    padding: "20px 14px",
    textAlign: "center",
    color: "#9ca3af",
    fontSize: 13,
  },
  footer: {
    padding: "6px 14px",
    borderTop: "1px solid #f1f1f1",
    background: "#fafbfc",
    fontSize: 11,
    color: "#9ca3af",
    textAlign: "center",
  },
  clearBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 18,
    height: 18,
    borderRadius: 5,
    border: "none",
    background: "#e5e7eb",
    cursor: "pointer",
    padding: 0,
    flexShrink: 0,
    color: "#6b7280",
  },
  addBtn: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "9px 14px",
    cursor: "pointer",
    width: "100%",
    border: "none",
    borderTop: "1px solid #f1f1f1",
    background: "#eff6ff",
    textAlign: "left",
    fontSize: 13,
    fontFamily: "inherit",
    color: "#1d4ed8",
    fontWeight: 600,
  },
  addBtnQuote: { color: "#111827", fontWeight: 700 },
  addBtnHint: {
    marginLeft: "auto",
    flexShrink: 0,
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: ".05em",
    color: "#2563eb",
    background: "#dbeafe",
    borderRadius: 5,
    padding: "2px 6px",
  },
  triggerCustom: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 8px",
    borderRadius: 6,
    background: "#dbeafe",
    color: "#1d4ed8",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: ".04em",
    textTransform: "uppercase",
    flexShrink: 0,
  },
};

/* Small plus icon for the "add custom" affordance */
const PlusGlyph = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M5 12h14" /><path d="M12 5v14" />
  </svg>
);

/* inject keyframes once */
if (typeof document !== "undefined" && !document.getElementById("board-dd-anim")) {
  const style = document.createElement("style");
  style.id = "board-dd-anim";
  style.textContent = `@keyframes boardDdFadeIn{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}`;
  document.head.appendChild(style);
}

/**
 * Searchable board code dropdown — uses a portal so the dropdown
 * escapes any parent overflow:hidden containers.
 *
 * Props:
 *   value      — current board code (e.g. "HB")
 *   onChange    — (code) => void
 *   placeholder — optional placeholder text
 *   className  — optional class on outer wrapper
 *   style      — optional inline style on outer wrapper
 *   codes      — optional subset of BOARD_CODES_FULL to render (default: all)
 *   allowCustom — when true, lets the user add the typed board as a new option
 *                 if it doesn't already exist (create-on-the-fly)
 */
const BoardCodeDropdown = ({
  value,
  onChange,
  placeholder = "Select board type…",
  className = "",
  style: outerStyle,
  codes,
  allowCustom = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [hoverIdx, setHoverIdx] = useState(-1);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const dropdownRef = useRef(null);

  const allCodes = codes || BOARD_CODES_FULL;
  const selected = allCodes.find((b) => b.code === value) || null;
  // A value that isn't a known board code is a user-added custom board.
  const isCustomValue = !selected && !!value;

  /* ── Filtered list ───────────────────────────────────── */
  const { filtered, flatItems } = useMemo(() => {
    const q = search.toLowerCase().trim();
    const matched = q
      ? allCodes.filter(
          (b) =>
            b.code.toLowerCase().includes(q) ||
            b.name.toLowerCase().includes(q) ||
            b.included.toLowerCase().includes(q)
        )
      : allCodes;

    const groups = [...new Set(matched.map((b) => b.group))];
    const grouped = groups.map((g) => ({
      group: g,
      items: matched.filter((b) => b.group === g),
    }));

    const flat = [];
    grouped.forEach((g) => g.items.forEach((b) => flat.push(b)));

    return { filtered: grouped, flatItems: flat };
  }, [allCodes, search]);

  /* ── Custom (create-on-the-fly) option ───────────────── */
  // Offer "Add «typed board»" when allowCustom is on, the user typed something,
  // and it doesn't already match an existing code or name.
  const trimmed = search.trim();
  const canAddCustom =
    allowCustom &&
    trimmed.length > 0 &&
    !allCodes.some(
      (b) =>
        b.code.toLowerCase() === trimmed.toLowerCase() ||
        b.name.toLowerCase() === trimmed.toLowerCase()
    );

  /* ── Position the dropdown relative to the trigger ──── */
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  /* ── Click outside (checks both trigger wrapper and portal dropdown) ── */
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        wrapRef.current && !wrapRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  /* ── Recalculate position on open and on scroll/resize ── */
  useEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, updatePosition]);

  /* ── Focus search on open ──────────────────────────── */
  useEffect(() => {
    if (open && searchRef.current) {
      // Small delay so portal is rendered first
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [open]);

  /* ── Keyboard ───────────────────────────────────────── */
  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === "Escape") {
      setOpen(false);
      setSearch("");
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHoverIdx((p) => Math.min(p + 1, flatItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHoverIdx((p) => Math.max(p - 1, 0));
    } else if (e.key === "Enter") {
      if (hoverIdx >= 0 && hoverIdx < flatItems.length) {
        e.preventDefault();
        onChange(flatItems[hoverIdx].code);
        setOpen(false);
        setSearch("");
      } else if (canAddCustom) {
        e.preventDefault();
        onChange(trimmed);
        setOpen(false);
        setSearch("");
      }
    }
  };

  /* ── Scroll hovered item into view ─────────────────── */
  useEffect(() => {
    if (!open || hoverIdx < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll("[data-board-item]");
    if (items[hoverIdx]) items[hoverIdx].scrollIntoView({ block: "nearest" });
  }, [hoverIdx, open]);

  const handleSelect = (code) => {
    onChange(code);
    setOpen(false);
    setSearch("");
  };

  /* ── Portal dropdown ───────────────────────────────── */
  const dropdownEl = open
    ? createPortal(
        <div
          ref={dropdownRef}
          style={{
            ...S.dropdown,
            top: pos.top,
            left: pos.left,
            width: pos.width,
          }}
          onKeyDown={handleKeyDown}
        >
          {/* Search */}
          <div style={S.searchWrap}>
            <span style={S.searchIcon}>{Icons.search}</span>
            <input
              ref={searchRef}
              style={S.searchInput}
              placeholder="Search code, name, or what's included…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setHoverIdx(-1); }}
            />
            {search && (
              <button style={S.clearBtn} type="button" onClick={() => setSearch("")}>
                {Icons.x}
              </button>
            )}
          </div>

          {/* List */}
          <div style={S.list} ref={listRef}>
            {flatItems.length === 0 && !canAddCustom ? (
              <div style={S.empty}>No board types match "{search}"</div>
            ) : (
              filtered.map((grp) => (
                <div key={grp.group}>
                  <div style={S.groupLabel}>{grp.group}</div>
                  {grp.items.map((b) => {
                    const idx = flatItems.indexOf(b);
                    const isHovered = idx === hoverIdx;
                    const isSelected = b.code === value;
                    return (
                      <button
                        key={b.code}
                        type="button"
                        data-board-item
                        style={{
                          ...S.item,
                          ...(isHovered ? S.itemHover : {}),
                          ...(isSelected ? S.itemSelected : {}),
                        }}
                        onMouseEnter={() => setHoverIdx(idx)}
                        onClick={() => handleSelect(b.code)}
                      >
                        <span style={S.itemCode}>{b.code}</span>
                        <div style={S.itemInfo}>
                          <div style={S.itemName}>{b.name}</div>
                          <div style={S.itemIncluded}>{b.included}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ))
            )}
            {canAddCustom && (
              <button
                type="button"
                style={S.addBtn}
                onMouseEnter={() => setHoverIdx(-1)}
                onClick={() => handleSelect(trimmed)}
              >
                <PlusGlyph />
                <span>Add <span style={S.addBtnQuote}>“{trimmed}”</span></span>
                <span style={S.addBtnHint}>new</span>
              </button>
            )}
          </div>

          {/* Footer */}
          <div style={S.footer}>
            {flatItems.length} board type{flatItems.length !== 1 ? "s" : ""}
            {search ? ` matching "${search}"` : " available"}
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div ref={wrapRef} style={{ ...S.wrap, ...outerStyle }} className={className} onKeyDown={handleKeyDown}>
      {/* ── Trigger ── */}
      <button
        ref={triggerRef}
        type="button"
        style={{ ...S.trigger, ...(open ? S.triggerOpen : {}) }}
        onClick={() => { setOpen(!open); setHoverIdx(-1); }}
      >
        {selected ? (
          <>
            <span style={S.triggerCode}>{selected.code}</span>
            <span style={S.triggerName}>{selected.name}</span>
            <span style={S.triggerIncluded}>{selected.included}</span>
          </>
        ) : isCustomValue ? (
          <>
            <span style={S.triggerCustom}>Custom</span>
            <span style={S.triggerName}>{value}</span>
          </>
        ) : (
          <span style={{ ...S.triggerName, ...S.triggerPlaceholder }}>{placeholder}</span>
        )}
        <span style={{ ...S.chevron, ...(open ? S.chevronOpen : {}) }}>{Icons.chevronDown}</span>
      </button>

      {dropdownEl}
    </div>
  );
};

export default BoardCodeDropdown;
